import React from 'react';
import loadable from '@loadable/component';

const PageBuilder = loadable(() =>
  import(/* webpackChunkName: "PageBuilder" */ '../PageBuilder/PageBuilder')
);

// Comprehensive Privacy Policy for Merrily
const fallbackPrivacyPolicy = `
*Last updated: March 2026*

## Privacy Policy for Merrily

VocalMist LLC ("we," "our," or "us") operates the Merrily platform. This Privacy Policy explains how we collect, use, share, and protect your personal information when you use our services.

## 1. Information We Collect

### Account Information
When you create an account, we collect:
- Name and email address
- Phone number for verification and communication
- Profile photo (optional)
- Address information for delivery coordination

### Payment Information
- Payment information is processed through Stripe
- We do not store credit card numbers or banking details
- Stripe handles all payment data according to PCI compliance standards
- We receive transaction confirmation data only

### Listing and Transaction Data
- Party kit descriptions, photos, and pricing information you provide
- Booking requests, messages, and transaction history
- Reviews and ratings given and received
- Handoff verification photos and documentation

### Technical Information
- Device and browser information
- IP address and general location data
- Usage patterns and platform interactions
- Cookies and similar tracking technologies

### Location Data
- General location for local search functionality
- Approximate addresses for delivery coordination
- We do not track precise real-time location

## 2. How We Use Your Information

### Platform Operations
- Facilitating connections between Providers and Renters
- Processing payments and managing transactions
- Providing customer support and resolving disputes
- Maintaining and improving platform functionality

### Communications
- Sending booking confirmations and transaction updates
- Facilitating communication between users
- Providing customer support responses
- Sending important account and policy notifications

### Safety and Security
- Verifying user identity and preventing fraud
- Investigating disputes and policy violations
- Protecting against unauthorized access
- Maintaining platform safety and security

### Analytics and Improvements
- Understanding platform usage patterns
- Improving search and matching algorithms
- Developing new features and services
- Generating aggregated, anonymized insights

### Marketing (Opt-in Only)
- Sending promotional communications (only with your consent)
- Providing personalized recommendations
- Sharing platform updates and new features

## 3. Information Sharing

### With Other Users
- Your name and profile information with transaction counterparties
- General location area (city/region) for local searches
- Contact information only when necessary for confirmed transactions
- **We never share your full address with Providers**

### With Service Providers
- **Stripe**: Payment processing (they handle all payment card data)
- **AWS**: Secure data storage and platform hosting
- **Delivery Providers**: Address information only when delivery is arranged
- **Customer Support Tools**: For providing user assistance

### Legal Compliance
- When required by law or legal process
- To protect our rights or the safety of users
- In connection with business transfers or mergers
- To investigate fraud or policy violations

### What We Don't Share
- We do not sell your personal information to third parties
- We do not share data with advertisers for targeting
- Full addresses are never shared with Providers
- Payment information stays with Stripe

## 4. Handoff Photos and Documentation

### Photo Storage and Usage
- Handoff photos are stored securely on AWS S3
- Photos are encrypted both in transit and at rest
- Used solely for dispute resolution and transaction verification
- Never shared publicly or used for marketing

### Retention Periods
- **Standard transactions**: Photos retained for 90 days post-completion
- **Disputed transactions**: Photos retained for 1 year for resolution purposes
- Photos are automatically deleted after retention periods
- Users may request immediate deletion of photos from resolved transactions

### Access Control
- Only authorized personnel can access handoff photos
- Access is logged and monitored
- Photos are only accessed for legitimate dispute resolution

## 5. Data Security

### Technical Safeguards
- All data transmitted using TLS encryption
- Data encrypted at rest using industry-standard methods
- Regular security audits and vulnerability assessments
- Access controls and authentication requirements

### Administrative Safeguards
- Employee training on data privacy and security
- Limited access to personal information on need-to-know basis
- Regular review of access permissions
- Incident response procedures for data breaches

## 6. Your Rights and Choices

### Access and Correction
- Request access to your personal information
- Update or correct inaccurate information through your account settings
- Request a copy of your data in portable format

### Deletion and Restriction
- Request deletion of your account and associated data
- Request restriction of processing in certain circumstances
- Note: Some information may be retained for legal compliance

### Communication Preferences
- Opt out of marketing communications at any time
- Manage notification preferences in your account settings
- You cannot opt out of essential service communications

### Account Control
- Delete your account at any time through settings
- Download your data before account deletion
- Some information may be retained as required by law

## 7. Cookies and Tracking

### Essential Cookies
- Required for platform functionality and security
- Session management and authentication
- Cannot be disabled without affecting platform use

### Analytics Cookies (Optional)
- Google Analytics for platform improvement (anonymized)
- User behavior analysis for feature development
- Can be disabled through browser settings

### What We Don't Use
- Third-party advertising cookies
- Social media tracking pixels
- Cross-site tracking for advertising purposes

## 8. Children's Privacy

Merrily is not intended for users under 18 years of age. We do not knowingly collect personal information from children under 18. If we become aware that we have collected information from a child under 18, we will promptly delete it.

## 9. California Privacy Rights (CCPA)

California residents have additional rights under the California Consumer Privacy Act:

### Right to Know
- Categories of personal information collected
- Sources of personal information
- Purposes for collection and sharing
- Categories of third parties we share with

### Right to Delete
- Request deletion of personal information (subject to legal exceptions)
- Confirm deletion has been completed

### Right to Opt-Out
- We do not sell personal information, but you can request we not share data for certain purposes

### Non-Discrimination
- We will not discriminate against you for exercising your privacy rights
- Same level of service regardless of privacy choices

To exercise these rights, contact us at privacy@rentmerrily.com.

## 10. International Data Transfers

If you access our services from outside the United States, your information may be transferred to and processed in the United States. We ensure appropriate safeguards are in place for international transfers.

## 11. Changes to This Policy

We may update this Privacy Policy periodically to reflect changes in our practices or legal requirements. We will:
- Notify users by email of material changes
- Post updated policy with effective date
- Provide summary of key changes

Continued use after changes constitutes acceptance of the updated policy.

## 12. Contact Information

For questions about this Privacy Policy or our privacy practices:

**VocalMist LLC - Privacy Team**  
Email: [privacy@rentmerrily.com](mailto:privacy@rentmerrily.com)  
Address: [Insert Physical Address]  
Phone: (805) 555-0123  

**Data Protection Officer**  
Email: [privacy@rentmerrily.com](mailto:privacy@rentmerrily.com)

We are committed to protecting your privacy and will respond to privacy inquiries within 30 days.
`;

// Create fallback content (array of sections) in page asset format:
export const fallbackSections = {
  sections: [
    {
      sectionType: 'article',
      sectionId: 'privacy',
      appearance: { fieldType: 'customAppearance', backgroundColor: '#ffffff' },
      title: { fieldType: 'heading1', content: 'Privacy Policy' },
      blocks: [
        {
          blockType: 'defaultBlock',
          blockId: 'hero-content',
          text: {
            fieldType: 'markdown',
            content: fallbackPrivacyPolicy,
          },
        },
      ],
    },
  ],
  meta: {
    pageTitle: {
      fieldType: 'metaTitle',
      content: 'Privacy policy page',
    },
    pageDescription: {
      fieldType: 'metaDescription',
      content: 'Privacy policy fetch failed',
    },
  },
};

// This is the fallback page, in case there's no Privacy Policy asset defined in Console.
const FallbackPage = props => {
  return <PageBuilder pageAssetsData={fallbackSections} {...props} />;
};

export default FallbackPage;
